import { useMemo, useState } from "react";
import { Boxes, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { useAuthStore } from "@/stores/auth.store";
import { useLines } from "@/features/lines/hooks";
import { useProducts } from "@/features/products/hooks";
import { ObjectivePicker } from "@/features/simulations/objective/ObjectivePicker";
import { objectivesFor } from "@/features/simulations/objective/objective-meta";
import { useSimulatableOrders } from "@/features/production-orders/hooks";
import { useStartSimulation } from "@/features/simulations/hooks-start-simulation";
import { useStartNewLineSandbox } from "@/features/simulations/sandbox/hooks/useStartSandbox";
import type { SimulationObjectiveChoice } from "@/types/sim-sandbox.types";
import { objectiveMissingLabel, objectiveReady } from "@/types/sim-sandbox.types";
import { LinePicker } from "./LinePicker";

// Caminho de entrada ÚNICO: onde + o que se quer saber, e Simular. Substitui os
// dois cards de entrada e o modal de linhas, que levavam o usuário a uma tela de edição onde ele
// era recebido por três bloqueios em vez de por uma pergunta.
// Papel: a listagem de simulações continua aberta a qualquer usuário autenticado; a AÇÃO
// de simular é que exige gestor. Por isso o bloco inteiro só aparece para gestor.
export function StartSimulation() {
  const isGestor = useAuthStore((s) => s.user?.role === "GESTOR");
  const linesQuery = useLines();
  const productsQuery = useProducts();
  const startNewLine = useStartNewLineSandbox();
  const { start, isPending } = useStartSimulation();

  const [lineId, setLineId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [objective, setObjective] = useState<SimulationObjectiveChoice | null>(null);

  const lines = useMemo(() => linesQuery.data ?? [], [linesQuery.data]);
  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data]);
  const line = lines.find((l) => l.id === lineId) ?? null;
  // Ordens da linha escolhida; só o objetivo de prazo as usa.
  const ordersQuery = useSimulatableOrders(lineId);

  // No objetivo de prazo o produto é o DA ORDEM. Perguntá-lo de novo, além de
  // redundante, deixava escolher um produto diferente do da OP: o servidor recusa isso com 409
  // ("o produto da Ordem é diferente do produto a simular") e o usuário não tinha como saber.
  const orderProductId =
    objective?.kind === "OP_ON_TIME" && objective.productionOrderId
      ? (ordersQuery.data?.find((o) => o.id === objective.productionOrderId)?.productId ?? null)
      : null;
  // Produto único da organização ⇒ preseleciona, para o caminho comum não pedir nada além do
  // objetivo. Com vários, o usuário escolhe (a simulação precisa saber o que a linha produz).
  const effectiveProductId =
    orderProductId ?? productId ?? (products.length === 1 ? (products[0]?.id ?? null) : null);

  if (!isGestor) return null;

  const blocked = !line
    ? "Escolha a linha a simular."
    : !effectiveProductId
      ? "Escolha o produto."
      : objectiveMissingLabel(objective);

  return (
    <section className="rounded-lg border bg-card p-4">
      <header className="mb-3">
        <h2 className="text-[15px] font-semibold">Simular</h2>
        <p className="text-[13px] text-muted-foreground">
          Escolha onde e o que você quer saber. A resposta vem sem você precisar montar nada.
        </p>
      </header>

      {linesQuery.isError || productsQuery.isError || ordersQuery.isError ? (
        // Falha de leitura NUNCA vira "lista vazia": sem este guard, um erro no fetch das ordens
        // renderizava "esta linha não tem Ordens de Produção", que é enganoso, não só feio.
        <Notify.QueryError
          error={linesQuery.error ?? productsQuery.error ?? ordersQuery.error}
          onRetry={() => {
            void linesQuery.refetch();
            void productsQuery.refetch();
            void ordersQuery.refetch();
          }}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <LinePicker
            lines={lines}
            products={products}
            lineId={lineId}
            productId={effectiveProductId}
            onLine={setLineId}
            onProduct={setProductId}
          />
          <div>
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              O que você quer saber?
            </p>
            <ObjectivePicker
              value={objective}
              onChange={setObjective}
              orders={ordersQuery.data ?? []}
              // Entrada pelo hub é sempre sobre uma linha real ⇒ o objetivo de prazo se aplica.
              options={objectivesFor({ hasBaseLine: true })}
            />
          </div>
        </div>
      )}

      <footer className="mt-4 flex flex-wrap items-center gap-3 border-t pt-3">
        <Button
          disabled={!!blocked || isPending}
          onClick={() => {
            if (!line || !effectiveProductId || !objective || !objectiveReady(objective)) return;
            void start({
              lineId: line.id,
              lineName: line.name,
              productId: effectiveProductId,
              objective,
            });
          }}
        >
          <Play className="h-4 w-4" />
          {isPending ? "Iniciando…" : "Simular"}
        </Button>
        {blocked ? <span className="text-xs text-muted-foreground">{blocked}</span> : null}
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => void startNewLine()}>
          <Boxes className="h-4 w-4" />
          Montar uma linha nova
        </Button>
      </footer>
    </section>
  );
}
